/**
 * Ale y Emi, lado a lado.
 *
 * La pantalla contesta "¿quién sacó más?", y eso se lee comparando columnas,
 * no leyendo dos parrafos. Una fila por concepto, una columna por socio, el
 * neto al pie y la diferencia dicha en una frase debajo.
 *
 * Todo en dolares: el encabezado lo declara una vez con el chip, asi que las
 * celdas van sin codigo (MASTER §3.5). El sueldo va en su propia fila, fuera
 * del neto: es un gasto del local, no plata que se reparte.
 */
import { ChipMoneda, Importe } from "@/components/money";
import { Aviso } from "@/components/ui/aviso";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableCellImporte,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { diferenciaEntreSocios, type ResumenSocio } from "./logica";

interface Fila {
  etiqueta: string;
  valor: (r: ResumenSocio) => number;
}

/** Los aportes restan: se muestran con signo, que es como entran en el neto. */
const FILAS: ReadonlyArray<Fila> = [
  { etiqueta: "Retiros en plata", valor: (r) => r.plataUsd },
  { etiqueta: "Retiros en mercadería", valor: (r) => r.mercaderiaUsd },
  { etiqueta: "Aportes", valor: (r) => -r.aportesUsd },
];

export function TarjetasSocios({ resumen }: { resumen: ReadonlyArray<ResumenSocio> }): JSX.Element {
  const diferencia = diferenciaEntreSocios(resumen);
  const hayMovimientos = resumen.some((r) => r.cantidad > 0);
  const haySueldo = resumen.some((r) => r.sueldoUsd > 0);

  return (
    <section aria-label="Resumen por socio" className="space-y-3">
      <Card padding="none" className="overflow-hidden">
        <Table densidad="compacta">
          <TableHeader>
            <TableRow>
              <TableHead>
                <span className="flex items-center gap-2">
                  En dólares <ChipMoneda moneda="USD" />
                </span>
              </TableHead>
              {resumen.map((r) => (
                <TableHead key={r.socio.id} className="text-right">
                  {r.socio.nombre}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {FILAS.map((f) => (
              <TableRow key={f.etiqueta}>
                <TableCell className="text-muted-foreground">{f.etiqueta}</TableCell>
                {resumen.map((r) => (
                  <TableCellImporte
                    key={r.socio.id}
                    monto={f.valor(r)}
                    moneda="USD"
                    codigo={false}
                  />
                ))}
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell>Neto retirado</TableCell>
              {resumen.map((r) => (
                <TableCellImporte key={r.socio.id} monto={r.netoUsd} moneda="USD" codigo={false} />
              ))}
            </TableRow>
            {haySueldo ? (
              <TableRow>
                <TableCell className="font-normal text-muted-foreground">
                  Sueldo, aparte del neto
                </TableCell>
                {resumen.map((r) => (
                  <TableCellImporte
                    key={r.socio.id}
                    monto={r.sueldoUsd}
                    moneda="USD"
                    codigo={false}
                  />
                ))}
              </TableRow>
            ) : null}
          </TableFooter>
        </Table>
      </Card>

      {diferencia && hayMovimientos ? (
        <Aviso tono={diferencia.diferenciaUsd === 0 ? "success" : "neutral"} compacto>
          {diferencia.diferenciaUsd === 0 ? (
            "Están parejos: los dos retiraron lo mismo, neto de aportes."
          ) : (
            <>
              {diferencia.mayor.socio.nombre} sacó{" "}
              <Importe monto={diferencia.diferenciaUsd} moneda="USD" tamano="sm" /> más que{" "}
              {diferencia.menor.socio.nombre} en este período.
            </>
          )}
        </Aviso>
      ) : null}
    </section>
  );
}
